<template>
    <div class="content-block consumables">
        <div class="options">
            <dx-button type="normal"
                :text="$t('general.resetFilter')"
                styling-mode="contained"
                @click="onResetState"
            />
        </div>
            
        <dx-data-grid
            class="data-grid dx-card"
            :data-source="dataSourceConfig"
            :remote-operations="remoteOperations"
            :syncLookupFilterValues="false"
            :height="gridHeight"
            @row-click="onRowClick"
            @content-ready="onContentReady"
            @context-menu-preparing="addMenuItems"
            @editor-preparing="onEditorPreparing"
            @initialized=initializedGrid
            ref="dataGrid"
        >
            <dx-paging :enabled="true" />
            <dx-pager
                :visible="true"
                :allowed-page-sizes="pageSize"
                display-mode="full"
                :show-page-size-selector="true"
                :show-info="true"
                :show-navigation-buttons="false"
            />

            <dx-filter-row :visible="true" />
            <dx-header-filter :visible="true" />
            <dx-filter-panel :visible="showFilterPanel" />
            <dx-sorting mode="multiple"/>
            <dx-state-storing
                :enabled="true"
                type="custom"
                :customLoad="stateLoad"
                :customSave="stateSave"
            />

            <dx-column
                data-field="partCode"
                :caption="$t('consumables.color')"
                :visible-index="0"
                :width="30"
                css-class="toner-icon"
                cell-template="color-toner-cell"
                :allow-header-filtering="false"
                :allow-filtering="false"
                :allow-sorting="false"
            />
            <template #color-toner-cell="{ data }">
                <div class="fill" :style="getFillStyle(data.value)"></div>
            </template>

            <dx-column
                data-field="partName"
                :caption="$t('consumables.name')"
                :visible-index="1"
                :width="120"
                :allow-header-filtering="false"
            />
            <dx-column
                data-field="partModelCode"
                :caption="$t('consumables.partModelCode')"
                :visible-index="2"
                :width="250"
                :allow-header-filtering="false"
            />
            <dx-column
                data-field="partSerialNumber"
                :caption="$t('consumables.partSerialNumber')"
                :visible-index="3"
                :width="250"
                :allow-header-filtering="false"
            />
            <dx-column
                data-field="level"
                :caption="$t('consumables.level')"
                :visible-index="4"
                :width="100"
                cell-template="progress-cell"
                :allow-header-filtering="false"
            />
            <template #progress-cell="{ data }">
                <div class="progress-cell">
                    <DxProgressBar
                        :class="getClassProgress(data.value)"
                        :min="0"
                        :max="100"
                        :value="data.value <= 4 ? 4 : data.value"
                        :showStatus="false"
                    />
                    <div class="level">{{ data.value + '%' }}</div>
                </div>
            </template>

            <dx-column
                data-field="modelCode"
                :caption="$t('consumables.model')"
                :visible-index="5"
                :width="260"
                :allow-header-filtering="false"
            />
            <dx-column
                data-field="hostName"
                :caption="$t('consumables.host')"
                :visible-index="6"
                :width="120"
                :allow-header-filtering="false"
            />
            <dx-column
                data-field="ip"
                :caption="$t('consumables.ip')"
                :visible-index="7"
                :width="125"
                :allow-header-filtering="false"
            />

            <dx-column
                data-field="locationName"
                :caption="$t('consumables.location')"
                :visible-index="8"
                :width="160"
                :allow-header-filtering="true"
            >
                <dx-lookup
                    :data-source="locationHeaderFilter"
                    value-expr="name"
                    display-expr="name"
                />
            </dx-column>

            <dx-column
                data-field="device.serialNumber"
                :caption="$t('consumables.serialNumber')"
                :visible-index="9"
                :width="120"
                :allow-header-filtering="false"
            />

            <dx-column
                data-field="device.inventoryNumber"
                :caption="$t('consumables.inventoryNumber')"
                :visible-index="10"
                :width="120"
                :allow-header-filtering="false"
            />

            <dx-column
                data-field="deviceType"
                :caption="$t('consumables.type')"
                :visible-index="11"
                :width="80"
                :allow-header-filtering="true"
            >
                <dx-lookup
                    :data-source="printerTypes"
                    value-expr="name"
                    display-expr="name"
                />
            </dx-column>

            <dx-column
                data-field="colorType"
                :caption="$t('consumables.colorful')"
                :visible-index="12"
                :width="90"
                :allow-header-filtering="true"
            >
                <dx-lookup
                    :data-source="colorTypes"
                    value-expr="name"
                    display-expr="name"
                />
            </dx-column>

            <dx-column
                data-field="paperFormat"
                :caption="$t('consumables.paper')"
                :visible-index="13"
                :width="80"
                :allow-header-filtering="true"
            >
                <dx-lookup
                    :data-source="paperFormats"
                    value-expr="name"
                    display-expr="name"
                />
            </dx-column>

            <dx-column
                data-field="refreshDate"
                :caption="$t('consumables.refreshDate')"
                :sort-index="13"
                :width="160"
                data-type="date"
                format="dd.MM.yyyy HH:mm:ss"
                :allow-header-filtering="false"
            />
        </dx-data-grid>

        <CustomColumnChooser
            :visible="visible"
            :columns="columns"
            :onApply="onApply"
            :onHiding="onHiding"
        />
    </div>
</template>

<script>
import { mapCacheActions } from "vuex-cache";

import baseView from "../base";
import appInfo from "../../app-info";

import { getConsumables, getUserLocations } from "@/services/api";
import DxDataGrid, {
    DxColumn, DxLookup, DxFilterRow, DxHeaderFilter, DxPaging, DxFilterPanel, DxPager, DxStateStoring,
    DxSorting
} from "devextreme-vue/data-grid";
import DxButton from "devextreme-vue/button";
import { DxProgressBar } from 'devextreme-vue/progress-bar';

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { getFillStyle, getClassProgress } from '@/services/cell-consumables.js';
import { AclHandler } from "@/services/acl-handler";

import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

export const dictDataSourceConfig = (data) => ({
  store: { data, type: "array", key: "id" },
  map: rec => ({ value: rec.id, ...rec })
});

export default {
    extends: baseView,
    mixins: [DataDrid],
    components: {
        DxDataGrid, DxColumn, DxLookup, DxFilterRow, DxHeaderFilter, DxPaging,
        DxFilterPanel, DxPager, CustomColumnChooser, DxStateStoring, DxProgressBar,
        DxButton, DxSorting
    },
    data() {
      return {
        isCloudMode: this.$store.getters.isCloudMode,
        remoteOperations: { groupPaging: true },
        dataSourceConfig: { store: this.createDataSource(getConsumables) },
        locationHeaderFilter: [],
        printerTypes: [],
        colorTypes: [],
        paperFormats: [],
      };
    },
    computed: {
        appUser() {
            return this.$store.getters.user;
        },
        aclUser() {
            return new AclHandler(this.appUser, this.isCloudMode);
        },
        showFilterPanel() {
            return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
        },
        sumGap() {
            return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
        },
        gridHeight() {
            const appHeightSize = this.$store.getters.appContainerSize.height;
            return (appHeightSize || 800) - this.sumGap;
        },
        gridBodyHeight() {
            return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
        },
    },
    methods: {
        ...mapCacheActions(["getPrinterTypes", "getColorTypes", "getPaperFormats"]),
        getFillStyle, getClassProgress,
        onResetState() {
            this.dataGrid.clearFilter();
        },
        onEditorPreparing(e) {
            this.applyFilterRowTimeout(e);
        },
        onContentReady(e) {
            this.addFitToPageHeightButton(e);
        },
        onRowClick(e) {
            if (window.getSelection().focusOffset !== window.getSelection().anchorOffset)
                return;

            if (e.event.metaKey || e.event.ctrlKey) {
                const routeData = this.$router.resolve({
                    name: "device",
                    params: { id: e.data.device.id }
                });
                window.open(routeData.href, "_blank");
            } else {
                this.$router.push({
                    name: "device",
                    params: { id: e.data.device.id }
                });
            }
        },
    },
    async created() {
        this.gridStateKey = 'gridConsumables';

        document.title = `${appInfo.title} - ${this.$t('menu.consumables')}`;
        this.$store.commit("setBreadcrumb", [{
            link: '/consumables',
            title: this.$t('menu.consumables')
        }]);

        this.locationHeaderFilter = dictDataSourceConfig(await getUserLocations());
        this.printerTypes = dictDataSourceConfig(await this.getPrinterTypes());
        this.colorTypes = dictDataSourceConfig(await this.getColorTypes());
        this.paperFormats = dictDataSourceConfig(await this.getPaperFormats());
    },
    beforeDestroy() {
        const state = this.dataGrid.state();
        this.stateSave(state);
    },
  };
</script>
  
<style lang="scss">

</style>